import { Header } from './MichelaLanding/Header'
import { Footer } from './MichelaLanding/Footer'

export function TermsOfService() {
    const lastUpdated = 'December 18, 2024'

    return (
        <div className="min-h-screen bg-paper text-ink">
            <Header />
            <main className="max-w-4xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
                <article className="prose prose-lg max-w-none">
                    <h1 className="font-display text-4xl sm:text-5xl font-semibold text-ink mb-4">
                        Terms of Service
                    </h1>
                    <p className="text-ink-tertiary text-lg mb-12">
                        Last updated: {lastUpdated}
                    </p>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            1. Acceptance of Terms
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Welcome to Michela. These Terms of Service ("Terms") govern your access to and use of the Michela mobile application, website, and related services (collectively, the "Service") provided by Michela ("we," "our," or "us").
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            By accessing or using the Service, you agree to be bound by these Terms and our{' '}
                            <a href="/privacy" className="text-ink underline hover:text-ink-secondary transition-colors">Privacy Policy</a>. If you do not agree to these Terms, you may not access or use the Service.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We reserve the right to modify these Terms at any time. We will provide notice of material changes by updating the "Last updated" date and, where appropriate, notifying you through the Service or via email. Your continued use of the Service after such modifications constitutes acceptance of the updated Terms.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            2. Description of Service
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Michela is an AI-powered personal styling service that helps you create outfits from your existing wardrobe. The Service includes:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li><strong>Wardrobe Management:</strong> Upload and organize photos of your clothing items</li>
                            <li><strong>AI Outfit Recommendations:</strong> Receive personalized outfit suggestions based on your wardrobe, preferences, weather, and occasion</li>
                            <li><strong>Color Analysis:</strong> Get personalized color palette recommendations based on your features</li>
                            <li><strong>Virtual Try-On:</strong> Preview outfits on your photo (available with Pro subscription)</li>
                            <li><strong>Lookbook:</strong> Save and organize your favorite outfits</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            The Service is provided "as is" and we make no guarantees regarding the accuracy, quality, or suitability of AI-generated recommendations. Outfit suggestions are meant as guidance and the final decision of what to wear remains yours.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            3. Eligibility
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            To use the Service, you must:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Be at least 13 years of age (or the minimum age required in your jurisdiction)</li>
                            <li>Have the legal capacity to enter into these Terms</li>
                            <li>Not be prohibited from using the Service under applicable laws</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            If you are under 18, you represent that you have your parent's or guardian's permission to use the Service and that they have agreed to these Terms on your behalf.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            4. User Accounts
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            To access certain features of the Service, you must create an account. You agree to:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Provide accurate, current, and complete information during registration</li>
                            <li>Maintain and promptly update your account information</li>
                            <li>Keep your password secure and confidential</li>
                            <li>Accept responsibility for all activities that occur under your account</li>
                            <li>Notify us immediately of any unauthorized access or security breach</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We reserve the right to suspend or terminate your account if we suspect unauthorized use or violation of these Terms.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            5. User Content
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            You retain ownership of all content you submit to the Service, including wardrobe photos, mirror photos, and profile information ("User Content"). By uploading User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, process, and display your User Content solely for the purpose of providing and improving the Service.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            You represent and warrant that:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>You own or have the necessary rights to upload the User Content</li>
                            <li>Your User Content does not violate any third-party rights</li>
                            <li>Your User Content does not contain illegal, harmful, or offensive material</li>
                            <li>Photos you upload are of yourself or items you own</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We do not use your photos to train AI models without your explicit consent. We may use aggregated, anonymized data to improve our services.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            6. Subscriptions and Payments
                        </h2>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            6.1 Subscription Plans
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Michela offers both free and paid subscription plans. The free plan provides basic features, while the Pro subscription unlocks additional features including virtual try-on, unlimited outfit generations, and more.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            6.2 Billing
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Paid subscriptions are billed in advance on a recurring basis (monthly or annually, depending on your selection). Your subscription will automatically renew unless you cancel before the renewal date.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            6.3 Price Changes
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We reserve the right to change subscription prices. Any price changes will apply to billing periods after we provide you with notice of the change.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            6.4 Refunds
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Subscription payments are generally non-refundable, except as required by applicable law. For subscriptions purchased through the App Store or Google Play, refund requests are subject to their respective policies.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            6.5 Cancellation
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            You may cancel your subscription at any time through your account settings or the App Store/Google Play. Cancellation will take effect at the end of your current billing period, and you will retain access to Pro features until then.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            7. Acceptable Use
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            You agree not to use the Service to:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Violate any applicable laws or regulations</li>
                            <li>Infringe on the intellectual property or privacy rights of others</li>
                            <li>Upload content that is illegal, harmful, threatening, abusive, or objectionable</li>
                            <li>Upload photos of other people without their consent</li>
                            <li>Attempt to gain unauthorized access to the Service or related systems</li>
                            <li>Interfere with or disrupt the Service or servers</li>
                            <li>Use automated systems (bots, scrapers) to access the Service without permission</li>
                            <li>Reverse engineer, decompile, or attempt to extract source code</li>
                            <li>Use the Service for any commercial purpose without our written consent</li>
                            <li>Misrepresent your identity or affiliation</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            8. Intellectual Property
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            The Service and its original content (excluding User Content), features, and functionality are owned by Michela and are protected by copyright, trademark, and other intellectual property laws.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            The Michela name, logo, and all related names, logos, product and service names, designs, and slogans are our trademarks. You may not use these marks without our prior written permission.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            9. AI-Generated Content
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            The Service uses artificial intelligence to generate outfit recommendations, virtual try-on images, and style advice. You acknowledge that:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>AI-generated content may not always be accurate, appropriate, or suitable for your needs</li>
                            <li>Virtual try-on images are approximations and may not reflect actual appearance</li>
                            <li>We do not guarantee any specific results from AI features</li>
                            <li>You are responsible for your final clothing choices and purchases</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            AI-generated images created from your content are considered part of the Service and may be stored to provide continuity of experience. You may delete AI-generated content through your account settings.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            10. Third-Party Services
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            The Service may contain links to or integrate with third-party websites or services. We are not responsible for the content, privacy policies, or practices of third-party sites. Your use of third-party services is at your own risk and subject to their respective terms.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            11. Disclaimer of Warranties
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed uppercase font-medium">
                            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We do not warrant that:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>The Service will be uninterrupted, secure, or error-free</li>
                            <li>Results obtained from the Service will be accurate or reliable</li>
                            <li>The quality of the Service will meet your expectations</li>
                            <li>Any errors in the Service will be corrected</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            12. Limitation of Liability
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed uppercase font-medium">
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, MICHELA AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Our total liability for any claims arising from your use of the Service shall not exceed the amount you paid us in the twelve (12) months preceding the claim, or $100 USD, whichever is greater.
                        </p>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Some jurisdictions do not allow the exclusion of certain warranties or limitations of liability. In such cases, the above limitations may not apply to you.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            13. Indemnification
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            You agree to indemnify, defend, and hold harmless Michela and its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Your use of the Service</li>
                            <li>Your User Content</li>
                            <li>Your violation of these Terms</li>
                            <li>Your violation of any third-party rights</li>
                        </ul>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            14. Termination
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            You may terminate your account at any time by using the account deletion feature in the app or by contacting us. Upon termination:
                        </p>
                        <ul className="list-disc pl-6 text-ink-secondary mb-4 space-y-2">
                            <li>Your right to access the Service will cease immediately</li>
                            <li>Your User Content will be deleted in accordance with our Privacy Policy</li>
                            <li>Any outstanding subscription fees are non-refundable</li>
                        </ul>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Reasons for termination may include violation of these Terms, fraudulent activity, or extended periods of inactivity.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            15. Dispute Resolution
                        </h2>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            15.1 Informal Resolution
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Before filing a formal dispute, you agree to contact us at{' '}
                            <a href="mailto:legal@michela.app" className="text-ink underline hover:text-ink-secondary transition-colors">
                                legal@michela.app
                            </a>{' '}
                            and attempt to resolve the dispute informally for at least 30 days.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            15.2 Governing Law
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Michela operates, without regard to conflict of law principles.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            15.3 Arbitration
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Any dispute arising from these Terms that cannot be resolved informally shall be settled by binding arbitration, except that either party may seek injunctive relief in court for intellectual property infringement or unauthorized access.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            16. General Provisions
                        </h2>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            16.1 Entire Agreement
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            These Terms, together with our Privacy Policy, constitute the entire agreement between you and Michela regarding the Service.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            16.2 Severability
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            16.3 Waiver
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            16.4 Assignment
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            You may not assign or transfer these Terms without our prior written consent. We may assign our rights and obligations without restriction.
                        </p>

                        <h3 className="font-display text-xl font-medium text-ink mb-3 mt-6">
                            16.5 Force Majeure
                        </h3>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control.
                        </p>
                    </section>

                    <section className="mb-12">
                        <h2 className="font-display text-2xl font-semibold text-ink mb-4">
                            17. Contact Us
                        </h2>
                        <p className="text-ink-secondary mb-4 leading-relaxed">
                            If you have any questions about these Terms, please contact us:
                        </p>
                        <ul className="list-none text-ink-secondary mb-4 space-y-2">
                            <li>
                                <strong>Email:</strong>{' '}
                                <a href="mailto:legal@michela.app" className="text-ink underline hover:text-ink-secondary transition-colors">
                                    legal@michela.app
                                </a>
                            </li>
                            <li>
                                <strong>General inquiries:</strong>{' '}
                                <a href="mailto:hello@michela.app" className="text-ink underline hover:text-ink-secondary transition-colors">
                                    hello@michela.app
                                </a>
                            </li>
                        </ul>
                    </section>

                    <section className="mb-12 p-6 bg-paper-alt rounded-lg">
                        <p className="text-ink-secondary leading-relaxed">
                            By using Michela, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                        </p>
                    </section>
                </article>
            </main>
            <Footer />
        </div>
    )
}
